import { useRef } from "react";

const INDUSTRIES = ["Tech", "Finance", "Healthcare", "Retail", "Consulting", "Education", "Other"];

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

export default function Signup({ companyName, setCompanyName, industry, setIndustry, location, setLocation, logoPreview, onLogoChange, firstName, setFirstName, lastName, setLastName, email, setEmail, password, setPassword, slug, submitting, error, onSubmit }) {
  const fileInputRef = useRef(null);
  const canSubmit = companyName.trim() && firstName.trim() && lastName.trim() && email.trim() && password.length >= 6;

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="mb-8">
        <p className="text-xs font-semibold text-emerald-400 tracking-widest uppercase mb-2">Final step · ⏱ 60 seconds</p>
        <h1 className="text-2xl md:text-3xl font-bold text-white">Create your account</h1>
        <p className="text-gray-400 text-sm mt-1">Set up your company and unlock your full culture profile</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Company */}
          <div className="bg-[#131b2e] border border-gray-700/40 rounded-xl p-5 space-y-5">
            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Company</h3>
            <div>
              <label className="block text-xs font-semibold text-gray-400 tracking-wider uppercase mb-2">Company name *</label>
              <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Acme Corp"
                className="w-full bg-[#1a2235] border border-gray-700/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 tracking-wider uppercase mb-2">Industry</label>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map((ind) => (
                  <button key={ind} type="button" onClick={() => setIndustry(ind)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${industry === ind ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-[#1a2235] border-gray-700/60 text-gray-400 hover:border-gray-600"}`}>
                    {ind}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 tracking-wider uppercase mb-2">Location</label>
                <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Paris, France"
                  className="w-full bg-[#1a2235] border border-gray-700/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 tracking-wider uppercase mb-2">Logo</label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => fileInputRef.current?.click()}
                    className="w-12 h-12 rounded-lg border-2 border-dashed border-gray-600 hover:border-emerald-500/50 flex items-center justify-center transition-colors shrink-0 overflow-hidden">
                    {logoPreview ? <img src={logoPreview} alt="" className="w-full h-full object-cover" /> : <span className="text-gray-500 text-lg">+</span>}
                  </button>
                  <p className="text-xs text-gray-500">PNG/SVG</p>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={onLogoChange} className="hidden" />
                </div>
              </div>
            </div>
          </div>

          {/* Account */}
          <div className="bg-[#131b2e] border border-gray-700/40 rounded-xl p-5 space-y-5">
            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Your account</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 tracking-wider uppercase mb-2">First name *</label>
                <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jane"
                  className="w-full bg-[#1a2235] border border-gray-700/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 tracking-wider uppercase mb-2">Last name *</label>
                <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe"
                  className="w-full bg-[#1a2235] border border-gray-700/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 tracking-wider uppercase mb-2">Work email *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@acmecorp.com"
                className="w-full bg-[#1a2235] border border-gray-700/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 tracking-wider uppercase mb-2">Password *</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 characters"
                className="w-full bg-[#1a2235] border border-gray-700/60 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all" />
            </div>
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          {slug && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-5 py-4">
              <p className="text-xs text-emerald-400 mb-1">✨ Your career page will be live at:</p>
              <p className="text-white font-mono text-sm">jobego.co/<span className="text-emerald-400">{slug}</span></p>
            </div>
          )}

          <button type="submit" disabled={!canSubmit || submitting}
            className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold px-8 py-3 rounded-lg transition-all flex items-center justify-center gap-2">
            {submitting ? (<><Spinner /> Creating account...</>) : (<>Create my account & unlock →</>)}
          </button>

          <p className="text-xs text-gray-500">
            By creating an account, you agree to our{" "}
            <a href="/privacy-policy" className="text-emerald-400/70 hover:text-emerald-400 underline underline-offset-2">Terms & Privacy Policy</a>
          </p>
        </form>

        {/* Preview card */}
        <div className="hidden lg:block">
          <div className="sticky top-8">
            <div className="bg-[#131b2e] border border-gray-700/40 rounded-2xl overflow-hidden">
              <div className="h-24 bg-gradient-to-r from-emerald-600/40 to-teal-700/30" />
              <div className="flex justify-center -mt-10">
                {logoPreview ? (
                  <img src={logoPreview} alt="" className="w-16 h-16 rounded-full border-4 border-[#131b2e] object-cover bg-[#1a2235]" />
                ) : (
                  <div className="w-16 h-16 rounded-full border-4 border-[#131b2e] bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xl font-bold">
                    {(companyName || "A").charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="p-5 pt-3 text-center">
                <h3 className="text-base font-semibold text-white mb-1">{companyName || "Your Company"}</h3>
                <p className="text-xs text-gray-400 mb-4">{[industry, location].filter(Boolean).join(" · ") || "Industry · Location"}</p>
                <div className="space-y-2 mb-4 px-4">
                  <div className="h-2 bg-gray-700/50 rounded-full w-full" />
                  <div className="h-2 bg-gray-700/50 rounded-full w-4/5 mx-auto" />
                  <div className="h-2 bg-gray-700/50 rounded-full w-3/5 mx-auto" />
                </div>
                <div className="bg-[#0b1121]/50 border border-gray-700/30 rounded-lg py-3 px-4">
                  <p className="text-xs text-emerald-400 italic">✓ Culture profile ready to unlock</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
